import { line, scaleLinear } from 'd3';
import { arrayOrDefault } from 'lib/utils/arrayOrDefault';
import { useMemo } from 'react';
import { SymptomCardData } from '../types';
import { resolveColorIndexFromScale } from './graph-brainfunction-barchart';
import { formatNumber } from 'lib/utils/formatters/normalise';

export const SCATTER_HISTORICAL_CHART_CLASSNAME = 'scatter-historical-chart';

const NODE_COUNT = 5;
const LIVENODE_COUNT = NODE_COUNT - 1;
export function ScatterHistoricalChart(props: {
  data: SymptomCardData[];
  scale: number[];
  colors: string[];
  showValues?: boolean;
}) {
  const radius = 13;
  const margin = radius + 2;
  const width = 200;
  const height = 40;
  type EnhancedSymptomCardData =
    | (SymptomCardData & { type: 'normal' })
    | { type: 'missing' };

  /**
   * Form the dataset
   * Steps:
   * 1. Take the last 4 assessments, or mark the data as "mising"
   * 2. Add a final "missing" node
   */
  const data: EnhancedSymptomCardData[] = useMemo(
    () => [
      // Take the last 4 assessments, or mark the data as "mising"
      ...arrayOrDefault(
        props.data
          .map(_ => ({ ..._, type: 'normal' as const }))
          .slice(-LIVENODE_COUNT),
        Array(LIVENODE_COUNT).fill({ type: 'missing' })
      ),
      // Add a final "missing" node
      { type: 'missing' }
    ],
    [props.data]
  );

  /**
   * BEGIN D3 UTILS
   */
  const xScale = scaleLinear([0, LIVENODE_COUNT], [0 + margin, width - margin]);
  const yScale = scaleLinear(
    [props.scale[0], props.scale.at(-1)!],
    [height - radius, radius]
  );
  const createLine = line<EnhancedSymptomCardData>(
    (_d, i) => xScale(i),
    _d => (_d.type === 'normal' ? yScale(_d.value) : height / 2)
  );
  /**
   * END D3 UTILS
   */
  return (
    <svg
      className="scatter-historical-chart"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="none"
        strokeWidth={1}
        stroke="#767679"
        d={createLine(data)!}
      />
      <g>
        {data.map((value, index) => {
          if (value.type === 'normal') {
            return (
              <NodeWithValue
                shape={value.assessmentType === 'baseline' ? 'rect' : 'circle'}
                key={index}
                colors={props.colors}
                radius={radius}
                value={value.value}
                showValue={props.showValues}
                scale={props.scale}
                cx={xScale(index)}
                cy={yScale(value.value)}
              />
            );
          } else {
            return (
              <NodeWithMissingValue
                key={index}
                radius={radius * 0.6}
                cy={height / 2}
                cx={xScale(index)}
              />
            );
          }
        })}
      </g>
    </svg>
  );
}
function NodeWithMissingValue(props: {
  radius: number;
  cx: number;
  cy: number;
}) {
  return (
    <g>
      <circle r={props.radius} cy={props.cy} cx={props.cx} fill={'#767679'} />
    </g>
  );
}

function NodeWithValue(props: {
  value: number;
  showValue?: boolean;
  colors: string[];
  scale: number[];
  radius: number;
  cx: number;
  cy: number;
  shape: 'rect' | 'circle';
}) {
  const colorIndex = resolveColorIndexFromScale(props.value, props.scale);
  const resolvedColor = props.colors.at(colorIndex) ?? 'black';
  return (
    <g>
      {props.shape === 'rect' ? (
        <g>
          <rect
            x={props.cx - props.radius}
            y={props.cy - props.radius}
            width={props.radius * 2}
            height={props.radius * 2}
            stroke={resolvedColor}
            strokeWidth={3}
          />
          <circle
            r={props.radius * 0.6}
            cy={props.cy}
            cx={props.cx}
            fill={resolvedColor}
          />
        </g>
      ) : (
        <circle
          r={props.radius}
          cy={props.cy}
          cx={props.cx}
          fill={resolvedColor}
        />
      )}
      {props.showValue && (
        <text
          fontFamily="Comfortaa"
          fill="white"
          fontSize={12}
          textAnchor="middle"
          y={props.cy + 12 / 2}
          x={props.cx}
        >
          {formatNumber(props.value)}
        </text>
      )}
    </g>
  );
}
